import { DestroyRef, inject, Injectable } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AppSessionService } from "@vadmin/account/auth";
import { environment } from "../../../../environments/environment";
import { HealthFeatures } from "../../health/feature-names";

type PosthogClient = (typeof import("posthog-js"))["default"];

@Injectable({
    providedIn: "root",
})
export class PosthogService {
    private readonly _appSession = inject(AppSessionService);
    private readonly _destroyRef = inject(DestroyRef);

    private _client?: PosthogClient;
    private _initialization?: Promise<void>;

    init(): void {
        if (this._initialization || !environment.posthogKey) {
            return;
        }

        this._initialization = this._loadClientAndIdentify();
    }

    private async _loadClientAndIdentify(): Promise<void> {
        const { default: posthog } = await import("posthog-js");

        posthog.init(environment.posthogKey, {
            api_host: environment.posthogHost,
            person_profiles: "identified_only",
        });
        this._client = posthog;

        this._appSession.onSessionChanged.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => this._identify());

        this._identify();
    }

    private _identify(): void {
        if (!this._client) {
            return;
        }

        if (this._appSession.isHostUser() || !this._appSession.userId) {
            this._client.reset();
            return;
        }

        this._client.identify(`${this._appSession.userId}`, {
            email: this._appSession.user?.emailAddress,
            plan: this._appSession.tenantInfo?.edition?.name,
            companyId: this._appSession.tenantId,
            roles: this._appSession.user?.roleNames?.join(", "),
            createdAt: this._appSession.user?.creationTime,
            meetDomain: abp.setting.get(HealthFeatures.MeetDomain),
        });
    }
}
